import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Snackbar, Alert, InputAdornment, IconButton, Box, Chip, Tooltip
} from '@mui/material';
import api from '../utils/api';
import { Visibility, VisibilityOff, Add, LockOpen, Warning } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RegisteredUsers = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const [openUnlock, setOpenUnlock] = useState(false);
    const [password, setPassword] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/auth/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
        }
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setOpenDelete(true);
    };

    const handleUnlockClick = (user) => {
        setSelectedUser(user);
        setOpenUnlock(true);
    };

    const handleDeleteSubmit = async () => {
        try {
            await api.delete(`/auth/users/${selectedUser._id}`, { data: { password } });
            setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
            fetchUsers();
            setOpenDelete(false);
            setPassword('');
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMsg = error.response?.data?.message || 'Failed to delete user';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
    };

    const handleUnlockSubmit = async () => {
        try {
            await api.post(`/auth/unlock-user/${selectedUser._id}`);
            setSnackbar({ open: true, message: `Account unlocked for ${selectedUser.name}`, severity: 'success' });
            fetchUsers();
            setOpenUnlock(false);
        } catch (error) {
            console.error('Error unlocking user:', error);
            const errorMsg = error.response?.data?.message || 'Failed to unlock user';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
    };

    // Check if user account is currently locked
    const isAccountLocked = (user) => {
        if (!user.accountLockedUntil) return false;
        return new Date(user.accountLockedUntil) > new Date();
    };

    // Format lock time remaining
    const getLockTimeRemaining = (user) => {
        if (!user.accountLockedUntil) return '';
        const lockUntil = new Date(user.accountLockedUntil);
        const now = new Date();
        if (lockUntil <= now) return '';

        const minutesRemaining = Math.ceil((lockUntil - now) / 60000);
        if (minutesRemaining < 60) {
            return `${minutesRemaining} min`;
        }
        const hoursRemaining = Math.floor(minutesRemaining / 60);
        return `${hoursRemaining}h ${minutesRemaining % 60}m`;
    };

    // Format last login date
    const formatLastLogin = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ fontWeight: 'bold', m: 0 }}
                >
                    Registered Users
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/register-user')}
                >
                    Register New User
                </Button>
            </Box>
            <TableContainer component={Paper} elevation={3} sx={{ mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Phone Number</strong></TableCell>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell><strong>Location</strong></TableCell>
                            <TableCell><strong>Last Login</strong></TableCell>
                            <TableCell><strong>Account Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => {
                            const locked = isAccountLocked(user);
                            const failedAttempts = user.failedLoginAttempts || 0;
                            const requiresPasswordChange = user.requiresPasswordChange || false;

                            return (
                                <TableRow key={user._id}>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.phoneNumber}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.role}
                                            size="small"
                                            color={user.role === 'owner' ? 'error' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{user.location || 'N/A'}</TableCell>
                                    <TableCell>{formatLastLogin(user.lastLogin)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {locked && (
                                                <Chip
                                                    icon={<Warning />}
                                                    label={`Locked (${getLockTimeRemaining(user)})`}
                                                    color="error"
                                                    size="small"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            )}
                                            {!locked && failedAttempts > 0 && (
                                                <Tooltip title={`${failedAttempts} failed login attempts. Account locks after 5 attempts.`}>
                                                    <Chip
                                                        label={`${failedAttempts}/5 Failed Logins`}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            )}
                                            {requiresPasswordChange && (
                                                <Chip
                                                    label="Password Change Required"
                                                    color="info"
                                                    size="small"
                                                />
                                            )}
                                            {!locked && failedAttempts === 0 && !requiresPasswordChange && (
                                                <Chip
                                                    label="Active"
                                                    color="success"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                            {locked && (
                                                <Button
                                                    onClick={() => handleUnlockClick(user)}
                                                    variant="contained"
                                                    color="warning"
                                                    size="small"
                                                    startIcon={<LockOpen />}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Unlock
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => handleDeleteClick(user)}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete User Dialog */}
            <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
                    </Typography>
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        margin="normal"
                        required
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
                    <Button onClick={handleDeleteSubmit} color="error" disabled={!password}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unlock User Dialog */}
            <Dialog open={openUnlock} onClose={() => setOpenUnlock(false)}>
                <DialogTitle>Unlock User Account</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to unlock the account for <strong>{selectedUser?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will reset failed login attempts and remove the account lock.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUnlock(false)}>Cancel</Button>
                    <Button onClick={handleUnlockSubmit} color="warning" variant="contained">
                        Unlock Account
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default RegisteredUsers;